import { SnackBarService } from '../../../services/snackbar.service';
import { ApplicationValue } from '../../../services/warmup/application-type.model';
import { ApplicationTypeConstants, WarmUpService } from '../../../services/warmup/warmup.service';
import { TrackingService } from '../../../shared/services/tracking/google-analytics-events.service';
import { SurveyQuestionType } from '../../../shared/models/survey/survey-question-type.enum';
import { MessageService } from './../../../services/messages.service';
import { TrackingConstants } from './../../../shared/services/tracking/tracking-constants.service';
import { SurveyManagementService } from './../survey-management.service';
import { SurveyQuestion } from '../../../shared/models/survey/survey-question.model';
import { SurveyTemplate } from '../../../shared/models/survey/survey-template.model';
import {ChangeDetectorRef, Component, OnInit, OnChanges, ViewContainerRef, OnDestroy} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormArray } from '@angular/forms/src/model';
import {MdDialogRef, MdDialogConfig, MdDialog} from '@angular/material';
import { Observable } from 'rxjs/Observable';
import {SurveyMcqOption} from '../../../shared/models/survey/survey-mcq-option.model';
import {SurveyMcqOptionAction} from '../../../shared/models/survey/survey-mcq-option-action.enum';
import {SurveyResponseListComponent} from '../response/survey-response-list.component';
import {SurveyResponse} from '../../../shared/models/survey/survey-response.model';


export enum SurveyTab {
  Question = <number>0,
  Responses = <number>1
}

@Component({
  selector: 'add-surveyquestion',
  templateUrl: 'surveyquestion.component.html',
  styleUrls: ['surveyquestion.component.css']
})

/**
 * Component to add new survey question.
 *
 * @author mohit.khurana@vernuso.com (Mohit Khurana)
 */
export class SurveyQuestionComponent implements OnInit, OnDestroy {
  selectedSurveyQuestion: SurveyQuestion;
  action: string;
  createSurveyQuestionForm: FormGroup;
  surveyQuestionTypes: ApplicationValue[];
  multipleChoiceQuestionType: boolean;
  selectedTemplate: SurveyTemplate;
  selectedTemplateName: string;
  arrMcqChoices: any[] = [0];
  mcqOptions: FormArray;
  actionType: string;
  SurveyMcqOptionAction: typeof SurveyMcqOptionAction = SurveyMcqOptionAction;
  surveyResponses: SurveyResponse[] = [];
  selectedTab: SurveyTab = SurveyTab.Question;
  disabled = false;
  updateInstances: false;
  SurveyQuestionType: typeof SurveyQuestionType = SurveyQuestionType;
  allQuestions: SurveyQuestion[] = [];

  formErrors = {
    'questionTitle': '',
    'questionHelpText': '',
    'questionType': '',
    'questionAnswer': '',
    'questionRequiredFlag': '',
    'questionChoices': '',
  };

  validationMessages = {
    'questionTitle': {
      'required': 'Required field value.',
    },
    'questionHelpText': {
      'required': 'Required field value.',
    },
    'questionType': {
      'required': 'Required field value.',
    },
    'questionAnswer': {
      'required': 'Required field value.',
    },
    'questionRequiredFlag': {
      'required': 'Required field value.',
    },
    'questionChoices': {
      'required': 'Required field value.',
    }
  };

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MdDialogRef<any>,
    private surveyManagementService: SurveyManagementService,
    private warmUpService: WarmUpService,
    private snackBarService: SnackBarService,
    private cdr: ChangeDetectorRef,
    private trackingService: TrackingService,
    public dialog: MdDialog,
    public viewContainerRef: ViewContainerRef) { }

  ngOnInit(): void {
    this.selectedTemplateName = this.selectedTemplate.title;
    this.buildForm();
    this.actionType = this.action === 'Add' ? 'New' : this.action;
    this.setUpSurveyQuestions();
    this.updateSurveyQuestionForm();
  }

  ngOnDestroy(): void {
    // this cleanup is kept in destroy specifically for 'delete' of a question where these values are set to this component
    // by SurveyQuestionList but are overriden by init.
    this.selectedTab = SurveyTab.Question;
    this.surveyResponses = [];
    this.disabled = false;
  }

  setUpSurveyQuestions() {
    const surveyQuestionTypes =
      this.warmUpService.getApplicationValue(ApplicationTypeConstants.SURVEY_QUESTION_TYPE);
    if (surveyQuestionTypes instanceof Observable) {
      surveyQuestionTypes.subscribe(
        response => {
          this.warmUpService.setApplicationTypeInLS(response);
          this.surveyQuestionTypes = response.values;
          this.cdr.detectChanges();
        }
      );
    } else {
      this.surveyQuestionTypes = surveyQuestionTypes;
    }
  }

  getQuestionTypeDetails(id: number) {
    return this.surveyQuestionTypes.find(questionType => questionType.id === id);
  }

  buildForm() {
    this.createSurveyQuestionForm = this.formBuilder.group({
      surveyTemplate: [{ value: this.selectedTemplateName, disabled: true }],
      questionTitle: ['', Validators.required],
      questionHelpText: ['', Validators.required],
      questionType: ['', Validators.required],
      questionRequiredFlag: ['', Validators.required],
      questionChoices: ['', Validators.required],
      mcqOptions: this.formBuilder.array([this.createOption()]),
      updateInstances: [true]
    });

    this.createSurveyQuestionForm.valueChanges.subscribe(
      data => this.onValueChanged(data));
    this.onValueChanged(); // (re)set validation messages now
  }

  createOption(): FormGroup {
    return this.formBuilder.group({
      id: 0,
      optionText: ['', Validators.required],
      action: [SurveyMcqOptionAction.ADD],
      sequence: 0
    });
  }

  addOption(): void {
    this.mcqOptions = this.createSurveyQuestionForm.get('mcqOptions') as FormArray;
    this.mcqOptions.push(this.createOption());
    this.cdr.detectChanges();
    this.handleMcqScroll();
  }

  removeOption(index: number): void {
    if (this.action == 'Add') {
      this.mcqOptions.removeAt(index);
    } else if (this.action == 'Edit') {
      this.mcqOptions = this.createSurveyQuestionForm.get('mcqOptions') as FormArray;
      (<FormArray>this.createSurveyQuestionForm.controls['mcqOptions']).at(index).patchValue({
        action: SurveyMcqOptionAction.DELETE
      });
    }
    this.cdr.detectChanges();
  }

  onValueChanged(data?: any) {
    if (!this.createSurveyQuestionForm) {
      return;
    }
    const form = this.createSurveyQuestionForm;

    for (const field in this.formErrors) {
      this.formErrors[field] = '';
      const control = form.get(field);
      if (control && control.touched && !control.valid) {
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + ' ';
        }
      }
    }
  }

  onQuestionTypeChange(questionType?: any) {
    if (this.filterQuestionTypeById(questionType) === 'Multiple choice') {
      this.multipleChoiceQuestionType = true;
    } else {
      this.multipleChoiceQuestionType = false;
      this.createSurveyQuestionForm.get('mcqOptions').reset();
    }
  }

  // @todo: This function is still to be worked on.
  onNumberOfMCQChoiceChange(numberOfChoices?: any) {
    // '+' here is to parse the value to an integer.
    const numberOfMcqChoices = +numberOfChoices;
    this.arrMcqChoices = Array(numberOfMcqChoices).fill(1).map((x, i) => i);
  }

  onSubmit({ value, valid }) {
    // delete is enabled only after responses are cleared
    if (this.action === 'Delete') {
      // let question-list, the originator of this component, handle deletion
      this.dialogRef.close('Delete');
      return;
    }
    const surveyTemplate = this.selectedTemplate;
    const surveyQuestion = new SurveyQuestion();
    if (this.action === 'Edit') {
      surveyQuestion.id = this.selectedSurveyQuestion.id;
    }
    surveyQuestion.questionText = value.questionTitle;
    surveyQuestion.helpText = value.questionHelpText;
    surveyQuestion.questionType =
      SurveyQuestionType[this.getQuestionTypeDetails(value.questionType).name];
    surveyQuestion.required = value.questionRequiredFlag;
    surveyQuestion.type = value.questionType;
    this.updateInstances = value.updateInstances;
    if (this.multipleChoiceQuestionType) {
      this.handleMcqEdit(value, surveyQuestion, surveyTemplate);
    } else {
      this.saveSurvey(surveyQuestion, surveyTemplate);
    }
  }

  private handleMcqEdit(value: any, surveyQuestion: SurveyQuestion, surveyTemplate: SurveyTemplate) {
    const options = [];
    const deletedOptions = [];
    for (let option of value.mcqOptions) {
      const op = new SurveyMcqOption();
      op.id = option.id;
      op.action = option.action;
      op.optionText = option.optionText;
      op.sequence = option.sequence;
      options.push(op);
      if (option.action === SurveyMcqOptionAction.DELETE) {
        deletedOptions.push(option.id);
      }
    }
    surveyQuestion.options = options;
    if (deletedOptions.length !== 0) {
      // if any option is deleted, check if that option was selected as an answer
      this.surveyManagementService.listSurveyResponsesForMcqQuestionsAnsweredWithOption(surveyQuestion.id, deletedOptions)
        .subscribe(answers => {
          if (answers.length > 0) {
            this.surveyResponses = answers;
            this.selectedTab = SurveyTab.Responses;
          } else {
            this.saveSurvey(surveyQuestion, surveyTemplate);
          }
        });
    } else {
      this.saveSurvey(surveyQuestion, surveyTemplate);
    }
  }

  private saveSurvey(surveyQuestion: SurveyQuestion, surveyTemplate: SurveyTemplate) {
    if (this.action === 'Add') {
      this.surveyManagementService.addSurveyQuestion(surveyQuestion, surveyTemplate.id, this.updateInstances)
        .subscribe(response => {
          if (response.flagResult) {
            this.dialogRef.close(response);
          }
          this.trackingService
            .emitEvent(
              TrackingConstants.SURVEY_MANAGEMENT,
              TrackingConstants.SAVE_SURVEY_QUESTION,
              TrackingConstants.SUCCESS, 1);
          this.callSnackBarService(response, this.action);
        }, err => {
          this.trackingService
            .emitEvent(
              TrackingConstants.SURVEY_MANAGEMENT,
              TrackingConstants.SAVE_SURVEY_QUESTION,
              TrackingConstants.FAILURE, 0);
          this.callSnackBarService(err, this.action);
        });
    } else if (this.action === 'Edit') {
      this.surveyManagementService.updateSurveyQuestion(surveyQuestion, surveyTemplate.id)
        .subscribe(response => {
          if (response.flagResult) {
            this.dialogRef.close(response);
          }
          this.trackingService
            .emitEvent(
              TrackingConstants.SURVEY_MANAGEMENT,
              TrackingConstants.SAVE_SURVEY_QUESTION,
              TrackingConstants.SUCCESS, 1);
          this.callSnackBarService(response, this.action);
        }, err => {
          this.trackingService
            .emitEvent(
              TrackingConstants.SURVEY_MANAGEMENT,
              TrackingConstants.SAVE_SURVEY_QUESTION,
              TrackingConstants.FAILURE, 0);
          this.callSnackBarService(err, this.action);
        });
    }
  }

  closeDialog(formResponse: Object): void {
    this.createSurveyQuestionForm.reset();
    this.dialogRef.close(formResponse);
  }

  getSurveyQuestionObject(questionTitle: string): Object {
    return this.surveyQuestionTypes.filter(function (obj) {
      return obj.name === questionTitle;
    });
  }

  callSnackBarService(response: any, operation: string) {
    this.snackBarService
      .showStatus(response,
      operation === 'Add'
      && MessageService.ADD_SURVEY_QUESTION_SUCCESS
      || MessageService.UPDATE_SURVEY_QUESTION_SUCCESS,
        MessageService.SURVEY_QUESTION_FAILURE);
  }

  updateSurveyQuestionForm(): void {
    if (this.selectedSurveyQuestion) {
      const surveyQuestionType = SurveyQuestionType[this.selectedSurveyQuestion.questionType];
      this.createSurveyQuestionForm.patchValue({
        surveyTemplate: this.selectedTemplate.title,
        questionTitle: this.selectedSurveyQuestion.questionText,
        questionHelpText: this.selectedSurveyQuestion.helpText,
        questionType: this.filterQuestionTypeByName(surveyQuestionType),
        questionRequiredFlag: this.selectedSurveyQuestion.required,
      });
      // Below condition is to handler MCQ Question type. It will create options at runtime.
      if (surveyQuestionType === 'Multiple choice') {
        this.multipleChoiceQuestionType = true;
        for (let i = 0; i < this.selectedSurveyQuestion.options.length; i++) {
          if (i !== 0) {
            this.addOption();
          }
          let option = this.selectedSurveyQuestion.options[i];
          (<FormArray>this.createSurveyQuestionForm.controls['mcqOptions']).at(i).patchValue({
            id: option.id,
            optionText: option.optionText,
            action: SurveyMcqOptionAction.EDIT,
            sequence: option.sequence
          });
        }
      }
    }
  }

  isSubmitDisabled(): boolean {
    return !this.isAddQuestionFormValid() || this.isSurveyResponseAvailable();
  }

  isSurveyResponseAvailable(): boolean {
    return this.surveyResponses.length > 0;
  }

  isAddQuestionFormValid(): boolean {
    if (this.multipleChoiceQuestionType) {
      return this.createSurveyQuestionForm.get('questionTitle').valid
        && this.createSurveyQuestionForm.get('questionHelpText').valid
        && this.createSurveyQuestionForm.get('questionType').valid;
    } else {
      return this.createSurveyQuestionForm.get('questionTitle').valid
        && this.createSurveyQuestionForm.get('questionHelpText').valid
        && this.createSurveyQuestionForm.get('questionType').valid;
    }
  }

  filterQuestionTypeByName(questionTypeName: string): number {
    return this.surveyQuestionTypes
      .find(questionType => questionType.name === questionTypeName).id;
  }

  filterQuestionTypeById(questionTypeId: number): string {
    return this.surveyQuestionTypes
      .find(questionType => questionType.id === questionTypeId).name;
  }

  get selectedTabIndex(): number {
    return this.selectedTab;
  }

  set selectedTabIndex(index: number) {
    this.selectedTab = SurveyTab[SurveyTab[index]];
    if (this.selectedTab === SurveyTab.Question &&
        this.selectedSurveyQuestion.questionType.toString() === SurveyQuestionType[SurveyQuestionType.MULTIPLE_CHOICE]) {
      this.handleMcqScroll();
    }
  }

  isDisabled(): boolean {
    return this.disabled;
  }

  onResponseClear() {
    this.surveyResponses = [];
  }

  getOtherTextQuestions(): SurveyQuestion[] {
    return this.allQuestions.filter(question =>
        question.questionType.toString() === SurveyQuestionType[SurveyQuestionType.PARAGRAPH_TEXT]
        && question.id !== this.selectedSurveyQuestion.id);
  }

  private handleMcqScroll() {
    let scrollArea = document.querySelector(".pop-scroll-field");
    if (scrollArea) {
      scrollArea.scrollTop = scrollArea.scrollHeight;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/survey/surveyquestion/surveyquestion.component.ts